import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Subscription, fromEvent, Observable, map, of } from 'rxjs';
import { PAGES } from 'src/app/shared/constants/navigation-map';
import { NavigationService } from 'src/app/services/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { VALIDATION_PATTERNS } from 'src/app/shared/constants/text-field';
import {
  FORBIDDEN_PHONES,
  forbiddenPhoneValidator,
} from 'src/app/shared/constants/phone-validator';
import { FormData, Customer, EventInfo, Agent } from 'src/app/services/data.model';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { CheckboxComponent } from 'src/app/shared/components/checkbox/checkbox.component';
import { marketingAttributes } from 'src/app/shared/constants/marketing-params';
import { dataParams } from 'src/app/shared/constants/data-params';
import { TimerService } from 'src/app/services/timer.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { BrandService } from 'src/app/services/brand.service';
import { BrandConfig } from 'src/app/shared/constants/brand.constants';

type Offer = {
  title: string;
  description: string;
};

@Component({
  selector: 'app-intro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormGroupComponent,
    ButtonComponent,
    CheckboxComponent,
    NgSelectModule,
  ],
  templateUrl: './intro.component.html',
  styleUrl: './intro.component.scss',
})
export class IntroComponent implements OnInit, OnDestroy {
  public selected = '';
  personalInfoForm: FormGroup;
  otherTextfieldMaxLength = 125;
  previouslyFilledData: {
    customer: Customer;
    zipcode: string;
    marketingConsent: boolean;
  };
  isEmbedded: boolean = false;
  marketingParams: any[];
  dataParams = null;
  brandConfig: BrandConfig;

  subscription: Subscription = new Subscription();
  marketTitle: string;
  eventInfo: EventInfo;
  isEventPage = false;
  isFieldAgentPage = false;
  agentForm: FormGroup;
  eventFieldAgentId;
  eventFieldAgentName;
  isEventInValid = false;
  otherParams = [];
  offer: Offer = null; // add offer according to the example below

  get isEmailRequired(): boolean {
    return !(this.isEventPage || !!this.dataParams?.isEvent);
  }

  // Offer example
  // offer: Offer = {
  //   title: 'Offer title',
  //   description: 'Offer description',
  // }
  agreementText = '';

  constructor(
    private navigationService: NavigationService,
    private route: ActivatedRoute,
    private dataService: DataService,
    private fb: FormBuilder,
    public nextButtonState: NextButtonStateService,
    private timerService: TimerService,
    private brandService: BrandService
  ) {}

  ngOnInit(): void {
    this.brandConfig = this.brandService.getCurrentBrandConfig();
    this.agreementText = this.brandConfig.content.agreementText;

    const brandSub = this.brandService.brand$.subscribe((brandConfig) => {
      this.brandConfig = brandConfig;
      this.agreementText = brandConfig.content.agreementText;
    });
    this.subscription.add(brandSub);

    this.navigationService.setHasOfferBar(!!this.offer);
    this.buildForm();
    this.buildAgentForm();
    history.pushState(null, '');

    const popStateSub = fromEvent(window, 'popstate').subscribe((_) => {
      history.pushState(null, '');
    });

    if (this.dataService.id) {
      const getDataSub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          this.prepopulateStepData(data);
        });
      this.subscription.add(getDataSub);
    }
    this.timerService.stop();
    this.timerService.removeAlert();
    const routeSub = this.route.data.subscribe((data) => {
      this.isEmbedded = data['embed'] || false;
      this.isEventPage = !!data['event'];
      this.isFieldAgentPage = !!data['fieldAgents'];
      this.updateEmailValidators();
      if (this.isEmbedded) {
        this.navigationService.setIsEmbedded(true);
      }
    });
    const paramsSub = this.route.queryParams.subscribe((params) => {
      this.marketingParams = Object.keys(params)
        .filter((key) => marketingAttributes.includes(key))
        .map((key) => ({ parameter: key, value: params[key] }));
      this.marketTitle = this.marketingParams.find((item: any) => item.parameter === 'market_name')?.value;
      this.dataParams = Object.keys(params)
        .filter((key) => dataParams.includes(key))
        .reduce((result, key) => {
          result[key] = params[key] === 'true' ? true : params[key] === 'false' ? false : params[key];
          return result;
        }, {});

      this.updateEmailValidators();

      if (Object.keys(this.dataParams).length !== 0) {
        this.prepopulateDataFromParams();
      }
      if(params['eventCampaignId']){
        this.getEventData(params['eventCampaignId'])
      }
      if(params['eventFieldAgentId']){
        this.eventFieldAgentId = params['eventFieldAgentId'];
      }
      this.otherParams = Object.keys(params)
      .filter((key) => !marketingAttributes.includes(key) && !dataParams.includes(key) && key !== 'id')
      .map((key) => ({
        paramName: key,
        paramValue: Array.isArray(params[key]) ? params[key].join(', ') : params[key] // Convert arrays to strings
      }));
    });
    this.subscription.add(popStateSub);
    this.subscription.add(routeSub);
    this.subscription.add(paramsSub);
  }

  getEventData(eventCampaignId: string){
    this.eventInfo = null;
    const eventSub = this.dataService.getEventInfo(eventCampaignId).subscribe((data: {campaign: EventInfo}) => {
      this.eventInfo = data.campaign;
      if(this.eventFieldAgentId){
        this.eventFieldAgentName = this.eventInfo?.fieldAgents?.find((agent:Agent) => agent.id === this.eventFieldAgentId)?.name;
      }
    }, error => {
      this.isEventInValid = true;
    });
    this.subscription.add(eventSub);
  }

  onClickNext = () => {
    const saveDataSub = this.saveData(PAGES.WINDOW_COUNT).subscribe();
    this.subscription.add(saveDataSub);
    this.personalInfoForm.markAllAsTouched();
  };

  goToFieldAgents = () =>{
    this.navigationService.navigateToPath('field-agents');
  }

  startRegularForm = () => {
    this.navigationService.navigateToPath('', {
      isEvent: this.dataParams.isEvent,
      eventCampaignId: this.dataParams.eventCampaignId,
      eventFieldAgentId: this.agentForm.value.agent, // to be adjusted after backend completed
    });
  }

  buildForm() {
    this.personalInfoForm = this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(VALIDATION_PATTERNS.text),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(VALIDATION_PATTERNS.text),
        ],
      ],
      primaryEmail: [
        '',
        this.isEmailRequired
          ? [Validators.required, Validators.pattern(VALIDATION_PATTERNS.email)]
          : [Validators.pattern(VALIDATION_PATTERNS.email)]
      ],
      primaryPhone: [
        '',
        [
          Validators.required, forbiddenPhoneValidator(FORBIDDEN_PHONES)
        ]
      ],
      zipcode: ['', Validators.required],
      contactPreference: [true, [Validators.required, Validators.requiredTrue]],
    });
  }

  private updateEmailValidators() {
    if (!this.personalInfoForm?.get('primaryEmail')) {
      return;
    }

    const primaryEmailControl = this.personalInfoForm.get('primaryEmail');
    const validators = this.isEmailRequired
      ? [Validators.required, Validators.pattern(VALIDATION_PATTERNS.email)]
      : [Validators.pattern(VALIDATION_PATTERNS.email)];

    primaryEmailControl.setValidators(validators);
    primaryEmailControl.updateValueAndValidity({ emitEvent: false });
  }

  buildAgentForm(){
    this.agentForm = this.fb.group({
      agent: [null, Validators.required],
    });
  }

  updateFormValues() {
    this.personalInfoForm.patchValue({
      firstName: this.previouslyFilledData?.customer?.firstName || '',
      lastName: this.previouslyFilledData?.customer?.lastName || '',
      primaryEmail: this.previouslyFilledData?.customer?.email || '',
      primaryPhone: this.previouslyFilledData?.customer?.phonePrimary || '',
      zipcode: this.previouslyFilledData?.zipcode || '',
      contactPreference: this.previouslyFilledData?.marketingConsent || '',
    });
  }

  prepopulateDataFromParams() {
    this.personalInfoForm.patchValue({
      firstName: this.dataParams?.firstName || '',
      lastName: this.dataParams?.lastName || '',
      primaryEmail: this.dataParams?.emailAddress || '',
      primaryPhone: this.dataParams?.phoneNumber || '',
      zipcode: this.dataParams?.addressZipcode || ''
    });
    if(this.dataParams?.firstName) this.personalInfoForm.get('firstName').markAsTouched();
    if(this.dataParams?.lastName) this.personalInfoForm.get('lastName').markAsTouched();
    if(this.dataParams?.emailAddress) this.personalInfoForm.get('primaryEmail').markAsTouched();
    if(this.dataParams?.phoneNumber) this.personalInfoForm.get('primaryPhone').markAsTouched();
    if(this.dataParams?.addressZipcode) this.personalInfoForm.get('zipcode').markAsTouched();
  }

  prepopulateStepData(data: FormData) {
    if (
      data?.appointmentRequest?.customer &&
      data?.appointmentRequest?.marketingAttribution
    ) {
      this.previouslyFilledData = {
        customer: data.appointmentRequest.customer,
        zipcode: data.appointmentRequest.property.zipcode,
        marketingConsent: this.previouslyFilledData?.marketingConsent || true,
      };
    }
    this.updateFormValues();
  }

  saveData(nextPage): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };
    stepsData.appointmentRequest.customer.firstName =
      this.personalInfoForm.value.firstName;
    stepsData.appointmentRequest.customer.lastName =
      this.personalInfoForm.value.lastName;
    stepsData.appointmentRequest.customer.email =
      this.personalInfoForm.value.primaryEmail || null;
    stepsData.appointmentRequest.customer.phonePrimary =
      this.personalInfoForm.value.primaryPhone;
    stepsData.appointmentRequest.property.zipcode =
      this.personalInfoForm.value.zipcode;
    stepsData.appointmentRequest.marketingConsent =
      this.personalInfoForm.value.contactPreference;
    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage;
    stepsData.sessionData = JSON.stringify(sessionData);
    stepsData.appointmentRequest.marketingAttribution.submissionPageUrlParameters =
      this.marketingParams;
    stepsData.appointmentRequest.marketingAttribution.referrerUri =
      document?.referrer || '';
    stepsData.appointmentRequest.marketingAttribution.submissionPageUrl =
      window?.location?.href || '';

    if(this.dataParams.isPPL) stepsData.appointmentRequest.isPPL = true;
    if(this.dataParams.externalAgentId) stepsData.appointmentRequest.externalAgentId = this.dataParams.externalAgentId;
    if(this.dataParams.isVerifyHomeOwnership) stepsData.appointmentRequest.isVerifyHomeOwnership = this.dataParams.isVerifyHomeOwnership;

    if(this.dataParams.isEvent) stepsData.appointmentRequest.isEvent = true;
    if(this.dataParams.eventCampaignId && this.dataParams.eventFieldAgentId) {
      stepsData.appointmentRequest.eventDetails = {
        eventCampaignId: this.dataParams.eventCampaignId,
        eventFieldAgentId: this.dataParams.eventFieldAgentId
      };
    }

    stepsData.appointmentRequest.originationSource = this.dataParams.originationSource || '';
    stepsData.appointmentRequest.leadId = this.dataParams.leadId || '';
    stepsData.appointmentRequest.otherParameters = this.otherParams;

    return this.dataService.saveStepsData(stepsData, true).pipe(
      map((res) => {
        if (this.isEmbedded) {
          let urlString = this.brandConfig.appointmentBaseUrl;
          urlString = `${urlString}${nextPage}?id=${res.id}`;
          for (let index = 0; index < this.marketingParams.length; index++) {
            if (index === 0 && this.marketingParams[index].parameter) {
              urlString = `${urlString}?${this.marketingParams[index].parameter}=${this.marketingParams[index].value}`;
            } else if (this.marketingParams[index].parameter) {
              urlString = `${urlString}&${this.marketingParams[index].parameter}=${this.marketingParams[index].value}`;
            }
          }
          window.open(urlString);
          return of(false);
        } else {
          this.navigationService.navigateToPath(nextPage, { id: res.id });
        }
        return false;
      })
    );
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
    this.navigationService.setHasOfferBar(false);
  }
}
